import { Coordinates, MarkNamespace, MarkerStyle } from "@bennara/shared";
import {
  createPointMarker,
  createPointMarkerMaterial,
} from "./utils/createPointMarker";
import {
  createShapeMarker,
  createShapeMarkerMaterial,
  getMarkShapeGeometry,
  getMarkShapeTexture,
  orientToSurface,
} from "./utils/createShapeMarker";
import * as THREE from "three";
import { PointMarkerUserData } from "./PointMarkers";

export class MovingMarker {
  private readonly group = new THREE.Group();
  private readonly sphereMaterial = createPointMarkerMaterial(
    window.colors.moleNew,
  );
  private readonly sphere = createPointMarker(this.sphereMaterial);
  private readonly shapeMaterial = createShapeMarkerMaterial(
    window.colors.moleNew,
    MarkNamespace.Type.MOLE,
  );
  private shape: THREE.Mesh | null = null;
  private shapeType: MarkNamespace.Type | null = null;
  private userData: PointMarkerUserData | null = null;

  constructor() {
    this.group.add(this.sphere);
    this.group.visible = false;
  }

  applyTheme(): void {
    const color = window.colors.moleNew;
    (this.sphereMaterial as THREE.MeshBasicMaterial).color.set(color);
    this.shapeMaterial.color.set(color);
  }

  getUserData(): PointMarkerUserData | null {
    return this.userData;
  }

  setUserData(userData: PointMarkerUserData): void {
    this.userData = userData;
    this.sphere.userData = userData;
    if (this.shape) this.shape.userData = userData;
  }

  getMarker(): THREE.Object3D {
    return this.group;
  }

  getPosition() {
    return this.group.position;
  }

  /**
   * Sphere, or the type's stamp in shapes mode.
   * A missing type stays a sphere so untyped points keep the original marker.
   */
  configure(style: MarkerStyle, type?: MarkNamespace.Type): void {
    const useShape = style === "shapes" && type != null;
    this.sphere.visible = !useShape;
    if (!useShape) {
      if (this.shape) this.shape.visible = false;
      this.group.quaternion.identity();
      return;
    }

    if (!this.shape || this.shapeType !== type) {
      if (!this.shape) {
        this.shape = createShapeMarker(this.shapeMaterial, type);
        this.group.add(this.shape);
      } else {
        this.shape.geometry = getMarkShapeGeometry(type);
      }
      this.shapeMaterial.map = getMarkShapeTexture(type);
      this.shapeMaterial.needsUpdate = true;
      this.shapeType = type;
      if (this.userData) this.shape.userData = this.userData;
    }
    this.shape.visible = true;
  }

  /** Align the stamp to the skin. Spheres ignore the normal. */
  orient(normal: THREE.Vector3 | null): void {
    if (!normal || this.sphere.visible) {
      this.group.quaternion.identity();
      return;
    }
    orientToSurface(this.group, normal);
  }

  show(): void {
    this.group.visible = true;
  }

  hide(): void {
    this.group.visible = false;
  }

  setPositionAndShow(coordinates: Coordinates): void {
    this.group.position.set(coordinates.x, coordinates.y, coordinates.z);
    this.group.visible = true;
  }
}
